import { useState, useEffect } from "react";
import {
  Users, Envelope, PaperPlaneTilt, Plus, Trash, ArrowUp, ArrowDown,
  PencilSimple, X, FloppyDisk, ChartBar, ArrowLeft
} from "@phosphor-icons/react";
import api from "@/lib/api";

// ─── Block Definitions ────────────────────────────────────────────────────────

const BLOCK_TYPES = [
  { type: "header",    label: "Başlık (Header)",     desc: "Marka + renk bloğu" },
  { type: "heading",   label: "Başlık Metni",         desc: "Büyük yazı başlığı" },
  { type: "paragraph", label: "Paragraf",             desc: "Metin içeriği" },
  { type: "image",     label: "Görsel",               desc: "Fotoğraf ekle" },
  { type: "button",    label: "Buton (CTA)",          desc: "Eylem butonu" },
  { type: "promo",     label: "Promo Kutusu",         desc: "Öne çıkan mesaj" },
  { type: "stats",     label: "İstatistik Satırı",    desc: "3 sütun rakam" },
  { type: "divider",   label: "Ayırıcı Çizgi",        desc: "" },
  { type: "spacer",    label: "Boşluk",               desc: "" },
  { type: "footer",    label: "Alt Bilgi (Footer)",   desc: "Adres + unsubscribe" },
];

const DEFAULT_DATA = {
  header:    { bg: "#EA580C", title: "Estem Sürücü Kursu", subtitle: "Gebze'nin Güvenilir Sürücü Eğitim Merkezi", logo: true },
  heading:   { text: "Duyurumuz var!", level: "h2", align: "left" },
  paragraph: { text: "Sayın kursiyerimiz, size önemli bir bilgi iletmek istedik.", align: "left" },
  image:     { url: "", alt: "", width: "100%", align: "center", link: "" },
  button:    { text: "Hemen Başvur", url: "https://www.estemsurucukursu.com/basla", bg: "#EA580C", align: "center" },
  promo:     { bg: "#FFF7ED", title: "Özel Fırsat", text: "Bu dönem kayıt yaptıranlara özel avantajlar!" },
  stats:     { items: [{ number: "33.000+", label: "Mezun Kursiyer" }, { number: "%87", label: "Başarı Oranı" }, { number: "37 Yıl", label: "Deneyim" }] },
  divider:   { color: "#E2E8F0" },
  spacer:    { height: 24 },
  footer:    { address: "Hacıhalil Mh. Gümgüm İş Merkezi Kat:1 · Gebze/Kocaeli", phone: "0262 646 3 646", unsubscribe: true },
};

const EMAIL_LOGO_URL = "https://www.estemsurucukursu.com/estem-logo.png";

// ─── Block Canvas Preview ─────────────────────────────────────────────────────

function BlockCanvas({ block, selected, isFirst, isLast, onClick, onEdit, onDelete, onMoveUp, onMoveDown }) {
  const { type, data } = block;

  const content = () => {
    switch (type) {
      case "header":
        return data.logo ? (
          <div style={{ background: "#FFFFFF", padding: "24px 24px 20px", textAlign: "center", borderBottom: `4px solid ${data.bg || "#EA580C"}` }}>
            <img src={EMAIL_LOGO_URL} alt="Estem Sürücü Kursu" width="130" style={{ display: "inline-block", maxWidth: "130px", height: "auto" }}/>
            {data.subtitle && <div style={{ fontSize: "13px", color: "#64748B", marginTop: "8px", fontFamily: "Arial,sans-serif" }}>{data.subtitle}</div>}
          </div>
        ) : (
          <div style={{ background: data.bg || "#EA580C", padding: "28px 24px", textAlign: "center" }}>
            <div style={{ fontWeight: "bold", fontSize: "22px", color: "white", fontFamily: "Arial,sans-serif" }}>{data.title}</div>
            {data.subtitle && <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.85)", marginTop: "6px", fontFamily: "Arial,sans-serif" }}>{data.subtitle}</div>}
          </div>
        );
      case "heading":
        return <div style={{ padding: "16px 24px 0", fontWeight: "bold", fontSize: data.level === "h2" ? "22px" : "18px", color: "#0F172A", fontFamily: "Arial,sans-serif", textAlign: data.align || "left" }}>{data.text}</div>;
      case "paragraph":
        return <div style={{ padding: "12px 24px", fontSize: "14px", color: "#334155", lineHeight: "1.7", fontFamily: "Arial,sans-serif", whiteSpace: "pre-wrap", textAlign: data.align || "left" }}>{data.text}</div>;
      case "image":
        return (
          <div style={{ padding: "14px 24px", textAlign: data.align || "center" }}>
            {data.url ? (
              <img src={data.url} alt={data.alt || ""} style={{ maxWidth: "100%", width: data.width || "100%", borderRadius: "8px" }}/>
            ) : (
              <div className="border-2 border-dashed border-stone-200 rounded-lg py-8 text-center text-stone-400 text-xs">Görsel seçilmedi — düzenlemek için tıklayın</div>
            )}
          </div>
        );
      case "button":
        return (
          <div style={{ textAlign: data.align || "center", padding: "20px 24px" }}>
            <span style={{ background: data.bg || "#EA580C", color: "white", padding: "12px 28px", borderRadius: "8px", fontWeight: "bold", fontSize: "14px", fontFamily: "Arial,sans-serif", display: "inline-block" }}>{data.text}</span>
          </div>
        );
      case "promo":
        return (
          <div style={{ background: data.bg || "#FFF7ED", padding: "18px 24px", margin: "8px 24px", borderRadius: "8px", borderLeft: "4px solid #EA580C" }}>
            <div style={{ fontWeight: "bold", fontSize: "15px", color: "#0F172A", fontFamily: "Arial,sans-serif" }}>{data.title}</div>
            <div style={{ fontSize: "13px", color: "#334155", marginTop: "6px", fontFamily: "Arial,sans-serif" }}>{data.text}</div>
          </div>
        );
      case "stats":
        return (
          <table style={{ width: "100%", borderTop: "1px solid #e5e7eb", borderBottom: "1px solid #e5e7eb" }}>
            <tbody><tr>{(data.items || []).map((item, i) => (
              <td key={i} style={{ textAlign: "center", padding: "14px" }}>
                <div style={{ fontSize: "20px", fontWeight: "bold", color: "#EA580C", fontFamily: "Arial,sans-serif" }}>{item.number}</div>
                <div style={{ fontSize: "11px", color: "#64748B", fontFamily: "Arial,sans-serif" }}>{item.label}</div>
              </td>
            ))}</tr></tbody>
          </table>
        );
      case "divider":
        return <hr style={{ border: "none", borderTop: `1px solid ${data.color || "#E2E8F0"}`, margin: "8px 24px" }}/>;
      case "spacer":
        return <div style={{ height: `${data.height || 24}px` }}></div>;
      case "footer":
        return (
          <div style={{ background: "#0F172A", padding: "24px", textAlign: "center" }}>
            <div style={{ fontSize: "12px", color: "#94A3B8", fontFamily: "Arial,sans-serif" }}>{data.address}</div>
            {data.phone && <div style={{ fontSize: "12px", color: "#94A3B8", marginTop: "4px", fontFamily: "Arial,sans-serif" }}>{data.phone}</div>}
            {data.unsubscribe && <div style={{ fontSize: "11px", color: "#64748B", marginTop: "8px", fontFamily: "Arial,sans-serif", textDecoration: "underline" }}>Abonelikten Çık</div>}
          </div>
        );
      default: return null;
    }
  };

  return (
    <div
      onClick={onClick}
      className={`relative group cursor-pointer transition-all ${selected ? "ring-2 ring-brand-primary ring-inset" : "hover:ring-1 hover:ring-stone-300 hover:ring-inset"}`}
    >
      {content()}
      {selected && (
        <div className="absolute top-1.5 right-1.5 flex gap-1 z-10">
          <button onClick={e => { e.stopPropagation(); onMoveUp(); }} disabled={isFirst} className="w-6 h-6 bg-white shadow-sm border border-stone-200 rounded flex items-center justify-center text-stone-500 hover:text-brand-ink disabled:opacity-30 transition-colors"><ArrowUp size={10} weight="bold"/></button>
          <button onClick={e => { e.stopPropagation(); onMoveDown(); }} disabled={isLast} className="w-6 h-6 bg-white shadow-sm border border-stone-200 rounded flex items-center justify-center text-stone-500 hover:text-brand-ink disabled:opacity-30 transition-colors"><ArrowDown size={10} weight="bold"/></button>
          <button onClick={e => { e.stopPropagation(); onEdit(); }} className="w-6 h-6 bg-brand-primary text-white shadow-sm rounded flex items-center justify-center hover:opacity-85 transition-opacity"><PencilSimple size={10} weight="bold"/></button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="w-6 h-6 bg-red-500 text-white shadow-sm rounded flex items-center justify-center hover:opacity-85 transition-opacity"><Trash size={10} weight="bold"/></button>
        </div>
      )}
    </div>
  );
}

// ─── Block Edit Modal ─────────────────────────────────────────────────────────

function AlignSelect({ value, onChange }) {
  return (
    <Field label="Hizalama">
      <div className="flex gap-2">
        {[["left","Sol"],["center","Orta"],["right","Sağ"]].map(([v,l]) => (
          <button key={v} type="button" onClick={()=>onChange(v)} data-testid={`align-${v}-btn`} className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${value===v ? "bg-brand-primary text-white border-brand-primary" : "border-stone-200 text-stone-500 hover:border-stone-300"}`}>{l}</button>
        ))}
      </div>
    </Field>
  );
}

function BlockEditModal({ block, onSave, onClose }) {
  const [data, setData] = useState({ ...block.data });
  const [uploading, setUploading] = useState(false);
  const upd = (k, v) => setData(d => ({ ...d, [k]: v }));

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: res } = await api.post("/admin/blog/upload-cover", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const base = process.env.REACT_APP_BACKEND_URL || "";
      upd("url", `${base}${res.url}`);
    } catch { /* silent */ }
    setUploading(false);
  };

  const form = () => {
    switch (block.type) {
      case "header":
        return (<>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!data.logo} onChange={e=>upd("logo",e.target.checked)} className="rounded" data-testid="header-logo-checkbox"/>
            Logo kullan (yazı yerine gerçek Estem logosu göster)
          </label>
          <Field label={data.logo ? "Alt Çizgi Rengi" : "Arka Plan Rengi"}><input type="color" value={data.bg||"#EA580C"} onChange={e=>upd("bg",e.target.value)} className="w-10 h-10 rounded cursor-pointer"/></Field>
          {!data.logo && <Field label="Başlık"><input type="text" value={data.title} onChange={e=>upd("title",e.target.value)} className="nb-input w-full"/></Field>}
          <Field label="Alt Başlık"><input type="text" value={data.subtitle||""} onChange={e=>upd("subtitle",e.target.value)} className="nb-input w-full" placeholder="İsteğe bağlı"/></Field>
        </>);
      case "heading":
        return (<>
          <Field label="Başlık Metni"><input type="text" value={data.text} onChange={e=>upd("text",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="Boyut">
            <select value={data.level} onChange={e=>upd("level",e.target.value)} className="nb-input w-full">
              <option value="h2">Büyük (H2)</option>
              <option value="h3">Orta (H3)</option>
            </select>
          </Field>
          <AlignSelect value={data.align||"left"} onChange={v=>upd("align",v)}/>
        </>);
      case "paragraph":
        return (<>
          <Field label="Metin"><textarea rows={6} value={data.text} onChange={e=>upd("text",e.target.value)} className="nb-input w-full resize-none"/></Field>
          <AlignSelect value={data.align||"left"} onChange={v=>upd("align",v)}/>
        </>);
      case "image":
        return (<>
          <Field label="Görsel">
            <div className="space-y-2">
              {data.url && <img src={data.url} alt="" className="w-full h-32 object-cover rounded-lg border border-stone-200"/>}
              <label className="nb-btn-outline !text-xs !py-2 w-full text-center cursor-pointer block">
                {uploading ? "Yükleniyor..." : "Bilgisayardan Yükle"}
                <input type="file" accept="image/*" className="hidden" data-testid="image-block-upload-input" onChange={e=>e.target.files[0] && uploadImage(e.target.files[0])}/>
              </label>
              <input type="url" value={data.url||""} onChange={e=>upd("url",e.target.value)} placeholder="veya görsel URL'si yapıştırın" className="nb-input w-full !text-xs" data-testid="image-block-url-input"/>
            </div>
          </Field>
          <Field label="Alternatif Metin (SEO/erişilebilirlik)"><input type="text" value={data.alt||""} onChange={e=>upd("alt",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="Tıklanınca Gidilecek Link (isteğe bağlı)"><input type="url" value={data.link||""} onChange={e=>upd("link",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="Genişlik">
            <select value={data.width||"100%"} onChange={e=>upd("width",e.target.value)} className="nb-input w-full">
              <option value="100%">Tam Genişlik</option>
              <option value="75%">%75</option>
              <option value="50%">%50</option>
              <option value="33%">%33</option>
            </select>
          </Field>
          <AlignSelect value={data.align||"center"} onChange={v=>upd("align",v)}/>
        </>);
      case "button":
        return (<>
          <Field label="Buton Metni"><input type="text" value={data.text} onChange={e=>upd("text",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="Bağlantı URL"><input type="url" value={data.url} onChange={e=>upd("url",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="Renk"><input type="color" value={data.bg||"#EA580C"} onChange={e=>upd("bg",e.target.value)} className="w-10 h-10 rounded cursor-pointer"/></Field>
          <AlignSelect value={data.align||"center"} onChange={v=>upd("align",v)}/>
        </>);
      case "promo":
        return (<>
          <Field label="Arka Plan Rengi"><input type="color" value={data.bg||"#FFF7ED"} onChange={e=>upd("bg",e.target.value)} className="w-10 h-10 rounded cursor-pointer"/></Field>
          <Field label="Başlık"><input type="text" value={data.title} onChange={e=>upd("title",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="İçerik"><textarea rows={3} value={data.text} onChange={e=>upd("text",e.target.value)} className="nb-input w-full resize-none"/></Field>
        </>);
      case "stats":
        return (<>{(data.items||[]).map((item,i) => (
          <Field key={i} label={`Sütun ${i+1}`}>
            <div className="flex gap-2">
              <input type="text" value={item.number} onChange={e=>upd("items",data.items.map((it,ix)=>ix===i?{...it,number:e.target.value}:it))} placeholder="33.000+" className="nb-input flex-1"/>
              <input type="text" value={item.label} onChange={e=>upd("items",data.items.map((it,ix)=>ix===i?{...it,label:e.target.value}:it))} placeholder="Mezun" className="nb-input flex-1"/>
            </div>
          </Field>
        ))}</>);
      case "footer":
        return (<>
          <Field label="Adres"><input type="text" value={data.address} onChange={e=>upd("address",e.target.value)} className="nb-input w-full"/></Field>
          <Field label="Telefon"><input type="text" value={data.phone||""} onChange={e=>upd("phone",e.target.value)} className="nb-input w-full"/></Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-2">
            <input type="checkbox" checked={!!data.unsubscribe} onChange={e=>upd("unsubscribe",e.target.checked)} className="rounded"/>
            Abonelikten çık linki ekle
          </label>
        </>);
      case "spacer":
        return <Field label="Yükseklik (px)"><input type="number" min={4} max={120} value={data.height||24} onChange={e=>upd("height",Number(e.target.value))} className="nb-input w-24"/></Field>;
      case "divider":
        return <Field label="Renk"><input type="color" value={data.color||"#E2E8F0"} onChange={e=>upd("color",e.target.value)} className="w-10 h-10 rounded cursor-pointer"/></Field>;
      default: return <p className="text-sm text-stone-400">Bu blok için düzenleme yok.</p>;
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
          <h3 className="font-heading font-bold text-lg">Bloğu Düzenle</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700 w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100"><X weight="bold" size={16}/></button>
        </div>
        <div className="px-6 py-5 overflow-y-auto flex-1 space-y-4">{form()}</div>
        <div className="px-6 py-4 border-t border-stone-100 flex justify-end gap-2">
          <button onClick={onClose} className="nb-btn-outline !px-4 !py-2 !text-sm">İptal</button>
          <button onClick={() => onSave(data)} className="nb-btn !px-4 !py-2 !text-sm">Kaydet</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-stone-500 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

// ─── Email Marketing Main Component ──────────────────────────────────────────

export function EmailMarketing() {
  const [view, setView] = useState("main");
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [audience, setAudience] = useState({ counts: { all: 0 }, courses: [] });

  // Builder state
  const [editId, setEditId] = useState(null);
  const [campaignName, setCampaignName] = useState("Yeni Kampanya");
  const [subject, setSubject] = useState("");
  const [target, setTarget] = useState("all");
  const [singleEmail, setSingleEmail] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [editingBlock, setEditingBlock] = useState(null);
  const [flashMsg, setFlashMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showTestInput, setShowTestInput] = useState(false);

  const flash = (msg, dur = 3500) => { setFlashMsg(msg); setTimeout(() => setFlashMsg(""), dur); };

  const load = () => {
    api.get("/admin/subscribers").then(r => setSubscribers(r.data)).catch(() => {});
    api.get("/admin/email-campaigns").then(r => setCampaigns(r.data)).catch(() => {});
    api.get("/admin/email-audience-counts").then(r => setAudience(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  // Block operations
  const addBlock = (type) => {
    const b = { id: crypto.randomUUID(), type, data: { ...DEFAULT_DATA[type] } };
    setBlocks(prev => [...prev, b]);
  };

  const deleteBlock = (id) => { setBlocks(b => b.filter(x => x.id !== id)); if (selectedId === id) setSelectedId(null); };

  const moveBlock = (id, dir) => {
    setBlocks(prev => {
      const arr = [...prev];
      const i = arr.findIndex(x => x.id === id);
      const j = dir === "up" ? i - 1 : i + 1;
      if (j < 0 || j >= arr.length) return prev;
      [arr[i], arr[j]] = [arr[j], arr[i]];
      return arr;
    });
  };

  const saveBlock = (id, newData) => {
    setBlocks(b => b.map(x => x.id === id ? { ...x, data: newData } : x));
    setEditingBlock(null);
  };

  const openBuilder = (campaign = null) => {
    if (campaign) {
      setEditId(campaign.id); setCampaignName(campaign.name); setSubject(campaign.subject);
      setTarget(campaign.target || "all"); setSingleEmail(campaign.single_email || ""); setBlocks(campaign.blocks || []);
    } else {
      setEditId(null); setCampaignName("Yeni Kampanya"); setSubject(""); setTarget("all"); setSingleEmail("");
      setBlocks([{ id: crypto.randomUUID(), type: "header", data: { ...DEFAULT_DATA.header } }]);
    }
    setSelectedId(null); setView("builder");
  };

  const saveCampaign = async () => {
    if (!subject.trim()) { flash("Konu satırını doldurun!"); return; }
    try {
      const payload = { name: campaignName, subject, target, single_email: target === "single" ? singleEmail : null, blocks };
      if (editId) { await api.patch(`/admin/email-campaigns/${editId}`, payload); flash("Kaydedildi!"); }
      else { const { data } = await api.post("/admin/email-campaigns", payload); setEditId(data.id); flash("Kaydedildi!"); }
      load();
    } catch { flash("Kaydetme hatası!"); }
  };

  const sendCampaign = async () => {
    if (!subject.trim()) { flash("Konu satırını doldurun!"); return; }
    if (target === "single" && !singleEmail.trim()) { flash("Alıcı e-posta adresini yazın!"); return; }
    const count = target === "single" ? 1 : (audience.counts?.[target] ?? 0);
    if (!window.confirm(`${count} kişiye gönderilecek. Onaylıyor musunuz?`)) return;
    setSending(true);
    try {
      const payload = { name: campaignName, subject, target, single_email: target === "single" ? singleEmail : null, blocks };
      let cid = editId;
      if (cid) { await api.patch(`/admin/email-campaigns/${cid}`, payload); }
      else { const { data: created } = await api.post("/admin/email-campaigns", payload); cid = created.id; setEditId(cid); }
      const { data } = await api.post(`/admin/email-campaigns/${cid}/send`);
      flash(`${data.sent} kişiye gönderildi!`, 5000);
      load();
    } catch { flash("Gönderim hatası!"); }
    setSending(false);
  };

  const testSend = async () => {
    if (!testEmail || !editId) return;
    try {
      await api.post(`/admin/email-campaigns/${editId}/test-send`, { to: testEmail });
      flash(`Test maili gönderildi: ${testEmail}`);
      setShowTestInput(false);
    } catch { flash("Test gönderme hatası!"); }
  };

  // ── Main View ──
  if (view === "main") {
    return (
      <div data-testid="email-marketing-main">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { v: subscribers.length, l: "Toplam Abone" },
            { v: campaigns.filter(c => c.sent_at).length, l: "Gönderilen" },
            { v: campaigns.filter(c => !c.sent_at).length, l: "Taslak" },
            { v: campaigns.reduce((a, c) => a + (c.sent_count || 0), 0), l: "Toplam Gönderim" },
          ].map((s, i) => (
            <div key={i} className="nb-card p-5">
              <div className="text-3xl font-heading font-bold text-brand-primary">{s.v}</div>
              <div className="text-sm text-stone-500 mt-1">{s.l}</div>
            </div>
          ))}
        </div>

        {/* Campaigns */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Kampanyalar</h3>
          <button onClick={() => openBuilder()} className="nb-btn !px-4 !py-2 !text-sm" data-testid="new-campaign-btn">
            <Plus weight="bold" size={14}/> Yeni Kampanya
          </button>
        </div>
        <div className="space-y-3 mb-10">
          {campaigns.length === 0 ? (
            <div className="nb-card p-10 text-center text-stone-400">
              <Envelope size={40} weight="duotone" className="mx-auto mb-3 text-stone-300"/>
              <p className="text-sm">Henüz kampanya yok. İlk kampanyanızı oluşturun.</p>
            </div>
          ) : campaigns.map(c => (
            <div key={c.id} className="nb-card p-5 flex items-center gap-4" data-testid={`campaign-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-brand-ink truncate">{c.name}</div>
                <div className="text-sm text-stone-500 mt-0.5 truncate">{c.subject}</div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-stone-400 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full font-medium ${c.sent_at ? "bg-green-50 text-green-700" : "bg-stone-100 text-stone-500"}`}>{c.sent_at ? "Gönderildi" : "Taslak"}</span>
                  {c.sent_count > 0 && <span>{c.sent_count} alıcı</span>}
                  {c.sent_at && <span>{new Date(c.sent_at).toLocaleString("tr-TR")}</span>}
                  {c.sent_at && c.stats && (
                    <span className="flex items-center gap-2" data-testid={`campaign-stats-${c.id}`}>
                      <span className="text-blue-600 font-medium">{c.stats.opened} açıldı</span>
                      <span className="text-emerald-600 font-medium">{c.stats.clicked} tıklandı</span>
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => openBuilder(c)} className="nb-btn-outline !px-3 !py-1.5 !text-xs">Düzenle</button>
                <button onClick={async () => { await api.delete(`/admin/email-campaigns/${c.id}`); load(); }} className="text-red-500 hover:bg-red-50 px-3 py-1.5 rounded-lg text-xs transition-colors">Sil</button>
              </div>
            </div>
          ))}
        </div>

        {/* Subscribers */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-bold text-xl text-brand-ink">Aboneler ({subscribers.length})</h3>
        </div>
        <div className="nb-card overflow-hidden">
          {subscribers.length === 0 ? (
            <div className="p-10 text-center text-stone-400">
              <Users size={40} weight="duotone" className="mx-auto mb-3 text-stone-300"/>
              <p className="text-sm">Henüz abone yok. Site popupı aracılığıyla aboneler toplanacak.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold text-stone-600">E-posta</th>
                    <th className="text-left px-5 py-3 font-semibold text-stone-600 hidden md:table-cell">Ad</th>
                    <th className="text-left px-5 py-3 font-semibold text-stone-600 hidden md:table-cell">Kayıt Tarihi</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s, i) => (
                    <tr key={s.id} className={`${i % 2 === 0 ? "bg-white" : "bg-stone-50/50"} border-b border-stone-100`}>
                      <td className="px-5 py-3 font-medium">{s.email}</td>
                      <td className="px-5 py-3 text-stone-600 hidden md:table-cell">{s.name || "—"}</td>
                      <td className="px-5 py-3 text-stone-400 hidden md:table-cell">{new Date(s.created_at).toLocaleDateString("tr-TR")}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={async () => { await api.delete(`/admin/subscribers/${s.id}`); setSubscribers(p => p.filter(x => x.id !== s.id)); }} className="text-red-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded-lg transition-colors"><Trash size={14}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Builder View (Full-screen overlay) ──
  return (
    <div className="fixed inset-0 z-50 bg-stone-100 flex flex-col" data-testid="email-builder">
      {editingBlock && (
        <BlockEditModal block={editingBlock} onSave={(d) => saveBlock(editingBlock.id, d)} onClose={() => setEditingBlock(null)}/>
      )}

      {/* Top Bar */}
      <div className="bg-white border-b border-stone-200 px-4 py-2.5 flex items-center justify-between gap-3 shrink-0 flex-wrap">
        <div className="flex items-center gap-3">
          <button onClick={() => setView("main")} className="flex items-center gap-1.5 text-stone-500 hover:text-brand-ink text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-stone-100 transition-colors">
            <ArrowLeft weight="bold" size={14}/> Geri
          </button>
          <input
            value={campaignName}
            onChange={e => setCampaignName(e.target.value)}
            className="font-semibold text-sm text-brand-ink bg-transparent border-b border-stone-200 focus:border-brand-primary focus:outline-none px-1 py-0.5 min-w-[140px]"
            placeholder="Kampanya adı..."
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-400">Konu:</span>
            <input value={subject} onChange={e => setSubject(e.target.value)} className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary/30 w-52" placeholder="E-posta konusu ({{first_name}})"/>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-400">Hedef:</span>
            <select value={target} onChange={e => setTarget(e.target.value)} className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none" data-testid="campaign-target-select">
              <option value="all">Tüm Aboneler + Lead'ler ({audience.counts?.all ?? 0})</option>
              {(audience.courses || []).map(c => (
                <option key={c.code} value={c.code}>{c.name} ({audience.counts?.[c.code] ?? 0})</option>
              ))}
              <option value="single">Tek Kişiye Gönder</option>
            </select>
            {target === "single" && (
              <input
                type="email"
                value={singleEmail}
                onChange={e => setSingleEmail(e.target.value)}
                placeholder="ornek@eposta.com"
                className="border border-stone-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary/30 w-52"
                data-testid="single-email-input"
              />
            )}
          </div>
          <button onClick={saveCampaign} className="nb-btn-outline !px-4 !py-2 !text-xs flex items-center gap-1.5">
            <FloppyDisk weight="bold" size={13}/> Kaydet
          </button>
          <div className="relative">
            <button onClick={() => setShowTestInput(!showTestInput)} className="nb-btn-outline !px-4 !py-2 !text-xs flex items-center gap-1.5">
              <Envelope weight="bold" size={13}/> Test Gönder
            </button>
            {showTestInput && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-stone-200 rounded-xl shadow-xl p-4 w-64 z-20">
                <p className="text-xs text-stone-500 mb-2">Test maili gönderilecek adres:</p>
                <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="test@email.com" className="nb-input w-full !py-2 !text-sm mb-2" autoFocus/>
                <button onClick={testSend} className="nb-btn !py-1.5 !text-xs w-full" data-testid="test-send-submit-btn">Gönder</button>
              </div>
            )}
          </div>
          <button onClick={sendCampaign} disabled={sending} className="nb-btn !px-5 !py-2 !text-xs flex items-center gap-1.5 disabled:opacity-60" data-testid="send-campaign-btn">
            <PaperPlaneTilt weight="bold" size={13}/> {sending ? "Gönderiliyor..." : `Kampanyayı Gönder (${target === "single" ? (singleEmail.trim() ? 1 : 0) : (audience.counts?.[target] ?? 0)})`}
          </button>
        </div>
      </div>

      {flashMsg && (
        <div className="bg-brand-success text-white text-sm text-center py-2 font-medium shrink-0">{flashMsg}</div>
      )}

      {/* 3-Panel Builder */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left: Block Palette */}
        <div className="w-44 bg-white border-r border-stone-200 overflow-y-auto shrink-0">
          <div className="px-3 py-2.5 text-[10px] font-bold tracking-widest text-stone-400 uppercase border-b border-stone-100 sticky top-0 bg-white">BLOKLAR</div>
          {BLOCK_TYPES.map(bt => (
            <button key={bt.type} onClick={() => addBlock(bt.type)} className="w-full text-left px-3 py-2.5 hover:bg-brand-primarySubtle/50 transition-colors border-b border-stone-50 group" data-testid={`add-block-${bt.type}`}>
              <div className="text-xs font-semibold text-stone-700 group-hover:text-brand-primary transition-colors leading-tight">{bt.label}</div>
              {bt.desc && <div className="text-[10px] text-stone-400 mt-0.5">{bt.desc}</div>}
            </button>
          ))}
        </div>

        {/* Center: Canvas */}
        <div className="flex-1 overflow-y-auto p-6 bg-stone-100">
          <div className="max-w-[600px] mx-auto bg-white shadow-lg rounded-sm min-h-[500px]">
            {blocks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 text-stone-400">
                <Envelope size={48} weight="duotone" className="mb-4 text-stone-300"/>
                <p className="text-sm font-medium">Soldan blok ekleyerek başlayın</p>
              </div>
            ) : (
              blocks.map((block, idx) => (
                <BlockCanvas
                  key={block.id}
                  block={block}
                  selected={selectedId === block.id}
                  isFirst={idx === 0}
                  isLast={idx === blocks.length - 1}
                  onClick={() => setSelectedId(selectedId === block.id ? null : block.id)}
                  onEdit={() => setEditingBlock(block)}
                  onDelete={() => deleteBlock(block.id)}
                  onMoveUp={() => moveBlock(block.id, "up")}
                  onMoveDown={() => moveBlock(block.id, "down")}
                />
              ))
            )}
          </div>
        </div>

        {/* Right: Block List */}
        <div className="w-52 bg-white border-l border-stone-200 overflow-y-auto shrink-0">
          <div className="px-3 py-2.5 text-[10px] font-bold tracking-widest text-stone-400 uppercase border-b border-stone-100 sticky top-0 bg-white">
            EMAIL BLOKLARI ({blocks.length})
          </div>
          {blocks.length === 0 ? (
            <p className="text-xs text-stone-400 p-4 text-center">Henüz blok yok</p>
          ) : blocks.map((block, idx) => {
            const def = BLOCK_TYPES.find(t => t.type === block.type);
            return (
              <div
                key={block.id}
                onClick={() => setSelectedId(selectedId === block.id ? null : block.id)}
                className={`flex items-center justify-between px-3 py-2.5 border-b border-stone-50 cursor-pointer group transition-colors ${selectedId === block.id ? "bg-brand-primarySubtle/60" : "hover:bg-stone-50"}`}
              >
                <span className="text-xs font-medium text-stone-700 flex-1 min-w-0 truncate">{def?.label || block.type}</span>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button onClick={e=>{e.stopPropagation();moveBlock(block.id,"up");}} disabled={idx===0} className="w-5 h-5 flex items-center justify-center text-stone-400 hover:text-brand-ink disabled:opacity-20 rounded"><ArrowUp size={10} weight="bold"/></button>
                  <button onClick={e=>{e.stopPropagation();moveBlock(block.id,"down");}} disabled={idx===blocks.length-1} className="w-5 h-5 flex items-center justify-center text-stone-400 hover:text-brand-ink disabled:opacity-20 rounded"><ArrowDown size={10} weight="bold"/></button>
                  <button onClick={e=>{e.stopPropagation();setEditingBlock(block);}} className="w-5 h-5 flex items-center justify-center text-stone-400 hover:text-brand-primary rounded"><PencilSimple size={10} weight="bold"/></button>
                  <button onClick={e=>{e.stopPropagation();deleteBlock(block.id);}} className="w-5 h-5 flex items-center justify-center text-stone-400 hover:text-red-500 rounded"><Trash size={10} weight="bold"/></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
